import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #accordionGroup() {
    return this.dataset.accordionGroup || null;
  }

  #controller = new AbortController();
  #orientationMediaQuery = window.matchMedia('(orientation: portrait)');

  connectedCallback() {
    const { signal } = this.#controller;

    // Only set default state if orientation attributes are present
    if (this.hasAttribute('open-by-default-on-portrait') || this.hasAttribute('open-by-default-on-landscape')) {
      this.#setDefaultOpenState();
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    this.#orientationMediaQuery.addEventListener('change', this.#handleOrientationChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    // Only update if orientation attributes are present
    if (this.hasAttribute('open-by-default-on-portrait') || this.hasAttribute('open-by-default-on-landscape')) {
      this.#setDefaultOpenState();
    }
  };

  /**
   * Handles the orientation change event.
   */
  #handleOrientationChange = () => {
    // Only update if orientation attributes are present
    if (this.hasAttribute('open-by-default-on-portrait') || this.hasAttribute('open-by-default-on-landscape')) {
      this.#setDefaultOpenState();
    }
  };

  /**
   * Sets the default open state of the accordion based on orientation attributes.
   * Supports `open-by-default-on-portrait` and `open-by-default-on-landscape` attributes.
   * Only called when orientation attributes are present.
   */
  #setDefaultOpenState() {
    const isPortrait = this.#orientationMediaQuery.matches;
    const shouldOpenOnPortrait = this.hasAttribute('open-by-default-on-portrait');
    const shouldOpenOnLandscape = this.hasAttribute('open-by-default-on-landscape');

    if (isPortrait) {
      this.details.open = shouldOpenOnPortrait;
    } else {
      this.details.open = shouldOpenOnLandscape;
    }
  }

  /**
   * When this accordion opens inside a group, close every other accordion in the same group.
   */
  #handleToggle = () => {
    if (!this.details.open || !this.#accordionGroup) return;

    /** @type {NodeListOf<AccordionCustom>} */
    const siblings = document.querySelectorAll(
      `accordion-custom[data-accordion-group="${this.#accordionGroup}"]`
    );

    for (const sibling of siblings) {
      if (sibling === this) continue;
      sibling.details.open = false;
    }
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
